// ... existing imports ...
import { useEffect, useState, useRef, useMemo } from "react";
import Header from "../components/Header";
import { FilterPanel } from "../components/revenue/FilterPanel";
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { Search } from 'lucide-react';
import { useData } from "../context/DataContext";
import { CacheStatus } from "../components/CacheStatus";
import { ExportButton } from "../components/ExportButton";

// Add TypeScript declaration for window object
declare global {
  interface Window {
    allTeamMembers?: any[];
  }
}

// Add sort configuration type
type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

// Add this helper function at the top with other helpers
function formatCurrency(amount: number): string {
  const formattedNumber = Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedNumber}€`;
}

// Define column widths for the new layout
const nameColumnWidth = 'minmax(200px, 2fr)';
const revenueColumnWidth = 'minmax(160px, 1.5fr)';
const percentageColumnWidth = 'minmax(120px, 1fr)';

// --- Main Component ---
export default function MaterializationPerClient() {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'company', direction: 'asc' });

  const { data, loadingProgress, checkAndRefreshCache } = useData();
  
  // Check and refresh cache when component mounts
  useEffect(() => {
    checkAndRefreshCache();
  }, [checkAndRefreshCache]);

  // Available options (derived from data)
  const [availableCreationYears, setAvailableCreationYears] = useState<number[]>([]);
  const [availableArrivalYears, setAvailableArrivalYears] = useState<number[]>([]);
  const [availableManagers, setAvailableManagers] = useState<string[]>([]);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [hotels, setHotels] = useState<string[]>([]);
  const [hotelGroups, setHotelGroups] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);

  // Applied state (what's currently filtering the data)
  const [appliedCreationYears, setAppliedCreationYears] = useState<Set<number>>(new Set());
  const [appliedArrivalYears, setAppliedArrivalYears] = useState<Set<number>>(new Set());
  const [appliedManagers, setAppliedManagers] = useState<Set<string>>(new Set());
  const [appliedSegments, setAppliedSegments] = useState<Set<string>>(new Set());
  const [appliedClients, setAppliedClients] = useState<Set<string>>(new Set());
  const [appliedHotels, setAppliedHotels] = useState<Set<string>>(new Set());
  const [appliedHotelGroups, setAppliedHotelGroups] = useState<Set<string>>(new Set());
  const [appliedArrivalMonths, setAppliedArrivalMonths] = useState<Set<number>>(new Set());
  const [appliedArrivalQuarters, setAppliedArrivalQuarters] = useState<Set<number>>(new Set());
  const [appliedCreationMonths, setAppliedCreationMonths] = useState<Set<number>>(new Set());
  const [appliedCreationQuarters, setAppliedCreationQuarters] = useState<Set<number>>(new Set());
  const [appliedMarkets, setAppliedMarkets] = useState<Set<string>>(new Set());

  // Pending state (what user is selecting in UI - not yet applied)
  const [pendingCreationYears, setPendingCreationYears] = useState<Set<number>>(new Set());
  const [pendingArrivalYears, setPendingArrivalYears] = useState<Set<number>>(new Set());
  const [pendingManagers, setPendingManagers] = useState<Set<string>>(new Set());
  const [pendingSegments, setPendingSegments] = useState<Set<string>>(new Set());
  const [pendingClients, setPendingClients] = useState<Set<string>>(new Set());
  const [pendingHotels, setPendingHotels] = useState<Set<string>>(new Set());
  const [pendingHotelGroups, setPendingHotelGroups] = useState<Set<string>>(new Set());
  const [pendingArrivalMonths, setPendingArrivalMonths] = useState<Set<number>>(new Set());
  const [pendingArrivalQuarters, setPendingArrivalQuarters] = useState<Set<number>>(new Set());
  const [pendingCreationMonths, setPendingCreationMonths] = useState<Set<number>>(new Set());
  const [pendingCreationQuarters, setPendingCreationQuarters] = useState<Set<number>>(new Set());
  const [pendingMarkets, setPendingMarkets] = useState<Set<string>>(new Set());

  // Add state for virtual all selection
  const [allClientsSelected, setAllClientsSelected] = useState(true);
  const [allHotelGroupsSelected, setAllHotelGroupsSelected] = useState(true);
  const [allHotelsSelected, setAllHotelsSelected] = useState(true);
  const [allMarketsSelected, setAllMarketsSelected] = useState(true);

  // Update selection handlers for pending filters
  const handleClientsChange = (newSelected: Set<string>) => {
    setPendingClients(newSelected);
    setAllClientsSelected(newSelected.size === 0);
  };

  const handleHotelGroupsChange = (newSelected: Set<string>) => {
    setPendingHotelGroups(newSelected);
    setAllHotelGroupsSelected(newSelected.size === 0);
  };

  const handleHotelsChange = (newSelected: Set<string>) => {
    setPendingHotels(newSelected);
    setAllHotelsSelected(newSelected.size === 0);
  };

  const handleMarketsChange = (newSelected: Set<string>) => {
    setPendingMarkets(newSelected);
    setAllMarketsSelected(newSelected.size === 0);
  };

  // Detect changes between pending and applied state
  const hasUnappliedChanges = useMemo(() => {
    const setsEqual = (a: Set<any>, b: Set<any>) => 
      a.size === b.size && [...a].every(x => b.has(x));
    
    return !setsEqual(pendingCreationYears, appliedCreationYears) ||
           !setsEqual(pendingArrivalYears, appliedArrivalYears) ||
           !setsEqual(pendingManagers, appliedManagers) ||
           !setsEqual(pendingSegments, appliedSegments) ||
           !setsEqual(pendingClients, appliedClients) ||
           !setsEqual(pendingHotels, appliedHotels) ||
           !setsEqual(pendingHotelGroups, appliedHotelGroups) ||
           !setsEqual(pendingArrivalMonths, appliedArrivalMonths) ||
           !setsEqual(pendingArrivalQuarters, appliedArrivalQuarters) ||
           !setsEqual(pendingCreationMonths, appliedCreationMonths) ||
           !setsEqual(pendingCreationQuarters, appliedCreationQuarters) ||
           !setsEqual(pendingMarkets, appliedMarkets);
  }, [
    pendingCreationYears, appliedCreationYears,
    pendingArrivalYears, appliedArrivalYears,
    pendingManagers, appliedManagers,
    pendingSegments, appliedSegments,
    pendingClients, appliedClients,
    pendingHotels, appliedHotels,
    pendingHotelGroups, appliedHotelGroups,
    pendingArrivalMonths, appliedArrivalMonths,
    pendingArrivalQuarters, appliedArrivalQuarters,
    pendingCreationMonths, appliedCreationMonths,
    pendingCreationQuarters, appliedCreationQuarters,
    pendingMarkets, appliedMarkets,
  ]);

  // Apply filters - copy pending to applied
  const handleApplyFilters = () => {
    setAppliedCreationYears(new Set(pendingCreationYears));
    setAppliedArrivalYears(new Set(pendingArrivalYears));
    setAppliedManagers(new Set(pendingManagers));
    setAppliedSegments(new Set(pendingSegments));
    setAppliedClients(new Set(pendingClients));
    setAppliedHotels(new Set(pendingHotels));
    setAppliedHotelGroups(new Set(pendingHotelGroups));
    setAppliedArrivalMonths(new Set(pendingArrivalMonths));
    setAppliedArrivalQuarters(new Set(pendingArrivalQuarters));
    setAppliedCreationMonths(new Set(pendingCreationMonths));
    setAppliedCreationQuarters(new Set(pendingCreationQuarters));
    setAppliedMarkets(new Set(pendingMarkets));
  };

  // --- NEW: Always initialize months/quarters to all values on very first render ---
  useEffect(() => {
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
  }, []);

  // Add initialization refs for filters that depend on data
  const filtersInitialized = useRef(false);

  // Ensure all filters are initialized with all options selected after data loads (only on first load)
  useEffect(() => {
    if (filtersInitialized.current) return;
    if (availableManagers.length === 0 || availableSegments.length === 0 || 
        availableCreationYears.length === 0 || availableArrivalYears.length === 0 ||
        clients.length === 0 || hotels.length === 0 || hotelGroups.length === 0 ||
        markets.length === 0) {
      return; // Wait for all data to load
    }

    // Initialize filters: All with ALL values selected
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clients);
    const allHotels = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarkets = new Set(markets);

    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);

    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    
    // Update "all selected" flags
    setAllClientsSelected(false); // Explicitly selected all
    setAllHotelsSelected(false); // Explicitly selected all
    setAllHotelGroupsSelected(false); // Explicitly selected all
    setAllMarketsSelected(false); // Explicitly selected all

    filtersInitialized.current = true;
  }, [availableManagers, availableSegments, clients, hotels, hotelGroups, markets, availableCreationYears, availableArrivalYears]);

  // After setData(allData); in fetchData or after data is loaded
  useEffect(() => {
    if (!data || data.length === 0) return;

    // Years
    const creationYears = Array.from(new Set(data.map(row => row.request_year).filter(Boolean))).sort((a, b) => b - a);
    const arrivalYears = Array.from(new Set(data.map(row => row.arrival_year).filter(Boolean))).sort((a, b) => b - a);

    // Managers
    const managers = Array.from(new Set(data.map(row => row.request_manager).filter(Boolean))).sort();

    // Segments
    const segments = Array.from(new Set(data.map(row => row.segment).filter(Boolean))).sort();

    // Clients
    const allClients = Array.from(new Set(data.map((row: any) => row.company).filter(Boolean))).sort();

    // Hotels/Venues
    const venues = Array.from(new Set(data.map(row => row.venue).filter(Boolean))).sort();

    // Hotel Groups
    let groups = Array.from(new Set(data.map(row => row.hotel_group_name).filter(Boolean))).sort();
    const hasNoGroup = data.some(row => !row.hotel_group_name);
    if (hasNoGroup) {
      groups = [...groups, 'No Group'];
    }

    // Markets
    let marketsList = Array.from(new Set(data.map(row => row.market).filter(Boolean))).sort();
    const hasNoMarket = data.some(row => !row.market);
    if (hasNoMarket) {
      marketsList = [...marketsList, 'No Market'];
    }

    setAvailableCreationYears(creationYears);
    setAvailableArrivalYears(arrivalYears);
    setAvailableManagers(managers);
    setAvailableSegments(segments);
    setClients(allClients);
    setHotels(venues);
    setHotelGroups(groups);
    setMarkets(marketsList);
  }, [data]);

  // Helper to check if a filter is in 'all' state (empty set or all options selected)
  function isAllSelected<T>(selected: Set<T>, allOptions: T[]) {
    return selected.size === 0 || selected.size === allOptions.length;
  }

  // Compute possible managers based on all other filters (cascading logic)

  // Repeat this pattern for all other possible... sets:
  // possibleHotelGroups, possibleHotels, possibleClients, possibleCreationYears, possibleCreationMonths, possibleCreationQuarters, possibleArrivalYears, possibleArrivalMonths, possibleArrivalQuarters

  // Date filters

  // Prune date filter sets when possible options change, but only update if changed and only if selection is non-empty and possible set is non-empty

  // Prune and auto-select possible values for Clients, Hotel Groups, and Hotels

  // Restore resetFilters function
  const resetFilters = () => {
    // Reset all filters to all values
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clients);
    const allHotels = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarkets = new Set(markets);
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    
    // Reset pending state
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);
    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    
    // Also reset applied state (so data refreshes immediately)
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    
    // Update "all selected" flags
    setAllClientsSelected(false);
    setAllHotelsSelected(false);
    setAllHotelGroupsSelected(false);
    setAllMarketsSelected(false);
  };

  // Filtered data using the search term and all other filters
  const filteredData = useMemo(() => {
    return data.filter(row => {
      // Search filter
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesSearch = 
          (row.destination_city && row.destination_city.toLowerCase().includes(lower)) ||
          (row.company && row.company.toLowerCase().includes(lower)) ||
          (row.request_manager && row.request_manager.toLowerCase().includes(lower)) ||
          (row.segment && row.segment.toLowerCase().includes(lower));
        if (!matchesSearch) return false;
      }

      // FilterPanel filters (use applied state)
      if (appliedManagers.size > 0 && !appliedManagers.has(row.request_manager)) return false;
      if (appliedClients.size > 0 && !appliedClients.has(row.company)) return false;
      if (appliedArrivalYears.size > 0 && !appliedArrivalYears.has(row.arrival_year)) return false;
      if (appliedCreationYears.size > 0 && !appliedCreationYears.has(row.request_year)) return false;
      if (appliedSegments.size > 0 && !appliedSegments.has(row.segment)) return false;
      const groupName = row.hotel_group_name ? row.hotel_group_name : 'No Group';
      if (appliedHotelGroups.size > 0 && !appliedHotelGroups.has(groupName)) return false;
      if (appliedHotels.size > 0 && !appliedHotels.has(row.venue)) return false;
      if (appliedArrivalMonths.size > 0 && !appliedArrivalMonths.has(row.arrival_month)) return false;
      if (appliedArrivalQuarters.size > 0 && !appliedArrivalQuarters.has(row.arrival_quarter)) return false;
      if (appliedCreationMonths.size > 0 && !appliedCreationMonths.has(row.request_month)) return false;
      if (appliedCreationQuarters.size > 0 && !appliedCreationQuarters.has(row.request_quarter)) return false;
      const marketName = row.market ? row.market : 'No Market';
      if (appliedMarkets.size > 0 && !appliedMarkets.has(marketName)) return false;

      return true;
    });
  }, [data, searchTerm, appliedManagers, appliedClients, appliedArrivalYears, appliedCreationYears, appliedSegments, appliedHotelGroups, appliedHotels, appliedArrivalMonths, appliedArrivalQuarters, appliedCreationMonths, appliedCreationQuarters, appliedMarkets]);

  // Group data by company, then city, then hotel
  const groupedData = useMemo(() => {
    // Structure: { [company]: { [city]: { [hotel]: [rows] } } }
    const result: Record<string, Record<string, Record<string, any[]>>> = {};
    filteredData.forEach(row => {
      const city = row.destination_city || 'Unknown City';
      if (!result[row.company]) result[row.company] = {};
      if (!result[row.company][city]) result[row.company][city] = {};
      if (!result[row.company][city][row.venue]) result[row.company][city][row.venue] = [];
      result[row.company][city][row.venue].push(row);
    });
    return result;
  }, [filteredData]);

  // --- Render helpers ---
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [expandedCities, setExpandedCities] = useState<Set<string>>(new Set());
  
  // Handle sort click
  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort data function
  const sortData = (data: any[]) => {
    return [...data].sort((a, b) => {
      let aValue: any = a[sortConfig.key];
      let bValue: any = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle strings
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      return sortConfig.direction === 'asc' 
        ? aString.localeCompare(bString) 
        : bString.localeCompare(aString);
    });
  };

  // Create flat data structure for sorting and total calculation
  const flatData = useMemo(() => {
    const data: any[] = [];
    Object.entries(groupedData).forEach(([company, cities]) => {
      const allCompanyRows = Object.values(cities).flatMap((hotels: any) => Object.values(hotels).flat()) as any[];
      // Filter to only include Booked requests (consistent with other materialization reports)
      const bookedCompanyRows = allCompanyRows.filter((r: any) => r.status === 'Booked');
      const estimatedRevenue = bookedCompanyRows.reduce((sum: number, r: any) => sum + (r.estimated_revenue || 0), 0);
      const realisedRevenue = bookedCompanyRows.reduce((sum: number, r: any) => sum + (r.realised_revenue || 0), 0);
      const materialisation = estimatedRevenue !== 0 ? (realisedRevenue / estimatedRevenue) * 100 : 0;
      
      if (estimatedRevenue === 0 && realisedRevenue === 0) return;
      
      data.push({
        company,
        cities,
        estimatedRevenue,
        realisedRevenue,
        materialisation
      });
    });
    return data;
  }, [groupedData]);

  // Apply sorting
  const sortedData = useMemo(() => sortData(flatData), [flatData, sortConfig]);

  // Calculate totals
  const totals = useMemo(() => {
    return sortedData.reduce((acc, row) => {
      acc.estimatedRevenue += (row.estimatedRevenue || 0);
      acc.realisedRevenue += (row.realisedRevenue || 0);
      return acc;
    }, {
      estimatedRevenue: 0,
      realisedRevenue: 0
    });
  }, [sortedData]);

  // Table data transformer for export (level 1 only - clients)
  const getTableDataForExport = () => {
    const tableData = sortedData.map(({ company, estimatedRevenue, realisedRevenue, materialisation }) => ({
      Client: company,
      'Estimated Revenue': estimatedRevenue,
      'Realised Revenue': realisedRevenue,
      'Materialisation %': materialisation
    }));

    // Add TOTAL row if there are 2 or more rows
    if (sortedData.length >= 2) {
      const totalMaterialisation = totals.estimatedRevenue !== 0 ? (totals.realisedRevenue / totals.estimatedRevenue) * 100 : 0;

      tableData.push({
        Client: 'TOTAL',
        'Estimated Revenue': totals.estimatedRevenue,
        'Realised Revenue': totals.realisedRevenue,
        'Materialisation %': totalMaterialisation
      });
    }

    return tableData;
  };

  function renderRows() {
    const rows: JSX.Element[] = [];
    
    sortedData.forEach(({ company, cities, estimatedRevenue, realisedRevenue, materialisation }) => {
      const isCompanyExpanded = expandedCompanies.has(company);
      rows.push(
        <tr key={company} className="bg-gray-50 hover:bg-gray-100 hover:cursor-pointer" onClick={() => {
          setExpandedCompanies(prev => {
            const next = new Set(prev);
            if (next.has(company)) next.delete(company); else next.add(company);
            return next;
          });
        }}>
          <td className="px-2 py-2 whitespace-nowrap text-sm font-semibold text-gray-900 min-w-[200px]">{company}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs">{formatCurrency(estimatedRevenue)}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs">{formatCurrency(realisedRevenue)}</td>
                            <td className="px-2 py-2 whitespace-nowrap text-xs">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${
                      materialisation === 0 ? 'bg-red-100 text-red-800' : 
                      materialisation > 0 && materialisation <= 20 ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-green-100 text-green-800'
                    }`}>
                      {materialisation.toFixed(1)}%
                    </span>
                  </td>
        </tr>
      );
      
      if (isCompanyExpanded) {
        // Sort cities alphabetically
        const sortedCities = Object.entries(cities).sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        sortedCities.forEach(([city, hotels]) => {
          const allCityRows = Object.values(hotels as any).flat() as any[];
          const bookedCityRows = allCityRows.filter((r: any) => r.status === 'Booked');
          const estimatedRevenue = bookedCityRows.reduce((sum: number, r: any) => sum + (r.estimated_revenue || 0), 0);
          const realisedRevenue = bookedCityRows.reduce((sum: number, r: any) => sum + (r.realised_revenue || 0), 0);
          const materialisation = estimatedRevenue !== 0 ? (realisedRevenue / estimatedRevenue) * 100 : 0;
          
          if (estimatedRevenue === 0 && realisedRevenue === 0) return;
          
          const cityKey = `${company}||${city}`;
          const isCityExpanded = expandedCities.has(cityKey);
          rows.push(
            <tr key={cityKey} className="bg-white hover:bg-gray-100 hover:cursor-pointer" onClick={() => {
              setExpandedCities(prev => {
                const next = new Set(prev);
                if (next.has(cityKey)) next.delete(cityKey); else next.add(cityKey);
                return next;
              });
            }}>
              <td className="pr-2 py-2 whitespace-nowrap text-xs text-gray-700 min-w-[200px] font-normal">
                <span className="mr-2 align-middle">{isCityExpanded ? <ChevronDownIcon className="h-4 w-4 inline" /> : <ChevronRightIcon className="h-4 w-4 inline" />}</span>
                {city}
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-xs">{formatCurrency(estimatedRevenue)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs">{formatCurrency(realisedRevenue)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${
                  materialisation === 0 ? 'bg-red-100 text-red-800' : 
                  materialisation > 0 && materialisation <= 20 ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-green-100 text-green-800'
                }`}>
                  {materialisation.toFixed(1)}%
                </span>
              </td>
            </tr>
          );
          
          if (isCityExpanded) {
            Object.entries(hotels as any).forEach(([hotel, hotelRows]) => {
              const bookedHotelRows = (hotelRows as any[]).filter((r: any) => r.status === 'Booked');
              const estimatedRevenue = bookedHotelRows.reduce((sum: number, r: any) => sum + (r.estimated_revenue || 0), 0);
              const realisedRevenue = bookedHotelRows.reduce((sum: number, r: any) => sum + (r.realised_revenue || 0), 0);
              const materialisation = estimatedRevenue !== 0 ? (realisedRevenue / estimatedRevenue) * 100 : 0;
              
              if (estimatedRevenue === 0 && realisedRevenue === 0) return;
              
              const hotelKey = `${company}||${city}||${hotel}`;
              rows.push(
                <tr key={hotelKey} className="bg-gray-50 border-l-4 border-blue-200">
                  <td className="pl-8 pr-2 py-2 whitespace-nowrap text-xs text-gray-700 min-w-[200px] font-normal">
                    <span className="flex items-center">
                      <BuildingOffice2Icon className="w-4 h-4 mr-1 text-blue-400 flex-shrink-0" />
                      {hotel}
                    </span>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">{formatCurrency(estimatedRevenue)}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">{formatCurrency(realisedRevenue)}</td>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${
                      materialisation === 0 ? 'bg-red-100 text-red-800' : 
                      materialisation > 0 && materialisation <= 20 ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-green-100 text-green-800'
                    }`}>
                      {materialisation.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              );
            });
          }
        });
      }
    });
    return rows;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Materialization per Client / City</h1>
          <div className="flex items-center space-x-4">
            <ExportButton
              reportName="Materialization per Client City"
              data={data}
              filters={{
                selectedCreationYears: appliedCreationYears,
                selectedArrivalYears: appliedArrivalYears,
                selectedManagers: appliedManagers,
                selectedSegments: appliedSegments,
                selectedClients: appliedClients,
                selectedHotels: appliedHotels,
                selectedHotelGroups: appliedHotelGroups,
                selectedArrivalMonths: appliedArrivalMonths,
                selectedArrivalQuarters: appliedArrivalQuarters,
                selectedCreationMonths: appliedCreationMonths,
                selectedCreationQuarters: appliedCreationQuarters,
                allClientsSelected: appliedClients.size === 0,
                allHotelsSelected: appliedHotels.size === 0,
                allHotelGroupsSelected: appliedHotelGroups.size === 0
              }}
              tableDataTransformer={getTableDataForExport}
            />
            <CacheStatus />
          </div>
        </div>
        <div className="mb-6">
          <FilterPanel
            selectedCreationYears={pendingCreationYears}
            selectedArrivalYears={pendingArrivalYears}
            selectedManagers={pendingManagers}
            selectedSegments={pendingSegments}
            selectedClients={pendingClients}
            selectedHotels={pendingHotels}
            selectedHotelGroups={pendingHotelGroups}
            selectedMarkets={pendingMarkets}
            availableCreationYears={availableCreationYears}
            availableArrivalYears={availableArrivalYears}
            availableManagers={availableManagers}
            availableSegments={availableSegments}
            clients={clients}
            hotels={hotels}
            hotelGroups={hotelGroups}
            markets={markets}
            setSelectedCreationYears={setPendingCreationYears}
            setSelectedArrivalYears={setPendingArrivalYears}
            setSelectedManagers={setPendingManagers}
            setSelectedSegments={setPendingSegments}
            resetFilters={resetFilters}
            selectedArrivalMonths={pendingArrivalMonths}
            selectedArrivalQuarters={pendingArrivalQuarters}
            setSelectedArrivalMonths={setPendingArrivalMonths}
            setSelectedArrivalQuarters={setPendingArrivalQuarters}
            selectedCreationMonths={pendingCreationMonths}
            selectedCreationQuarters={pendingCreationQuarters}
            setSelectedCreationMonths={setPendingCreationMonths}
            setSelectedCreationQuarters={setPendingCreationQuarters}
            onClientsChange={handleClientsChange}
            allClientsSelected={allClientsSelected}
            onHotelsChange={handleHotelsChange}
            allHotelsSelected={allHotelsSelected}
            onHotelGroupsChange={handleHotelGroupsChange}
            allHotelGroupsSelected={allHotelGroupsSelected}
            onMarketsChange={handleMarketsChange}
            allMarketsSelected={allMarketsSelected}
            onApplyFilters={handleApplyFilters}
            hasUnappliedChanges={hasUnappliedChanges}
          />
        </div>
        {loadingProgress && (
          <div className="mb-4 bg-blue-50 text-blue-700 p-2 rounded-md text-center">
            {loadingProgress}
          </div>
        )}
        <div className="flex gap-4 mb-4 items-end justify-between">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by destination city..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: nameColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: percentageColumnWidth }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('company')}
                  >
                    <div className="flex items-center gap-1">
                      CLIENT
                      {sortConfig?.key === 'company' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('estimatedRevenue')}
                  >
                    <div className="flex items-center gap-1">
                      ESTIMATED REVENUE
                      {sortConfig?.key === 'estimatedRevenue' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('realisedRevenue')}
                  >
                    <div className="flex items-center gap-1">
                      FINAL REVENUE
                      {sortConfig?.key === 'realisedRevenue' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('materialisation')}
                  >
                    <div className="flex items-center gap-1">
                      MATERIALISATION
                      {sortConfig?.key === 'materialisation' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {renderRows()}
                {/* Add the total row if there are 2 or more visible rows */}
                {sortedData.length >= 2 && (
                  <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                    <td className="px-2 py-1.5 text-sm text-gray-700">TOTAL</td>
                    <td className="px-2 py-1.5 text-xs">{formatCurrency(totals.estimatedRevenue)}</td>
                    <td className="px-2 py-1.5 text-xs">{formatCurrency(totals.realisedRevenue)}</td>
                    <td className="px-2 py-1.5 text-xs">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${
                        totals.estimatedRevenue === 0 ? 'bg-red-100 text-red-800' : 
                        (totals.realisedRevenue / totals.estimatedRevenue) * 100 > 0 && (totals.realisedRevenue / totals.estimatedRevenue) * 100 <= 20 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-green-100 text-green-800'
                      }`}>
                        {totals.estimatedRevenue === 0 ? '0.0' : ((totals.realisedRevenue / totals.estimatedRevenue) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}